import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { tokenService } from '../services/tokenService';

const FileViewer: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fileId) {
      setError('No file ID provided');
      setLoading(false);
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const action = queryParams.get('action') || 'view';
    const publicToken = queryParams.get('token');

    const handleFile = async () => {
      try {
        console.log('🔍 FileViewer Debug:');
        console.log('  - fileId:', fileId);
        console.log('  - publicToken from URL:', publicToken || 'none');
        console.log('  - action:', action);

        // ✅ If there's a public token, use it for public access
        if (publicToken) {
          console.log('✅ Public token found in URL - using public access');
          
          const apiUrl = process.env.REACT_APP_API_URL || 'http://qbx.co.za';
          
          // ✅ Direct download/view URL (no pop-up, no React routing)
          const fileUrl = `${apiUrl}/api/quotes/files/public/${publicToken}`;
          console.log('📄 Opening file:', fileUrl);
          
          // ✅ Navigate directly - no pop-up, no React routing
          window.location.href = fileUrl;
          setLoading(false);
          return;
        }

        // ✅ No public token - try authenticated access
        console.log('🔑 No public token - trying authenticated access');
        
        let token: string | null = tokenService.getToken();
        
        if (!token) {
          const storedToken = localStorage.getItem('token');
          if (storedToken) {
            token = storedToken;
            tokenService.setToken(storedToken);
          }
        }

        if (!token) {
          console.error('❌ No token found - redirecting to login');
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?returnUrl=${returnUrl}`;
          return;
        }

        // ✅ Regular authenticated access - direct download
        const apiUrl = process.env.REACT_APP_API_URL || 'http://qbx.co.za';
        
        // ✅ For both view and download, use the download endpoint
        const fileUrl = `${apiUrl}/api/quotes/files/download/${fileId}?token=${encodeURIComponent(token)}`;
        console.log('📄 Opening file from download URL:', fileUrl);
        
        // ✅ Navigate directly - no pop-up, no React routing
        window.location.href = fileUrl;
        setLoading(false);
        
      } catch (error: any) {
        console.error('Error fetching file:', error);
        setError(error.message || 'Could not open file. Please try again.');
        setLoading(false);
      }
    };

    handleFile();
  }, [fileId, location.search]);

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.close()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-screen">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-500">Loading file...</p>
      </div>
    </div>
  );
};

export default FileViewer;